import { Disclosure, Transition } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useMatch } from "react-router";

import { ChevronDown } from "@/assets/icons/ds";
import { Exit, LogoMonogram, QuestionMark } from "@/assets/icons/icons";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import {
  PortalNavigationItem,
  usePortalShellConfig,
} from "@/components/Layout/PortalShellConfig";
import { useLogout } from "@/hooks/useLogout";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";
import {
  getLegalLinksForSidebar,
  isLegalLinkWithAction,
} from "@/utils/navigation/legalLinks";

import { NavIcon } from "./NavIcon";
import { NavRow } from "./NavRow";

const NavItem = ({
  item,
  onNavigate,
  showFriendsWithBenefitsAttentionBubble,
}: {
  item: PortalNavigationItem;
  onNavigate?: () => void;
  showFriendsWithBenefitsAttentionBubble: boolean;
}) => {
  const { t } = useTranslation();
  const match = useMatch(item.target + "/*");

  return (
    <NavRow
      target={item.target}
      onClick={onNavigate}
      active={!!match}
      ariaCurrent={match ? "page" : undefined}
      label={t(item.text)}
      icon={
        <NavIcon
          item={item}
          showAttentionBubble={showFriendsWithBenefitsAttentionBubble}
        />
      }
    />
  );
};

/**
 * The navigation body shared by the desktop Sidebar (expanded state) and the
 * mobile/tablet burger drawer: nav items, support link, legal-links
 * disclosure and logout. Pass `onNavigate` to close a drawer after a click.
 *
 * Typography: SemiBold for all top-level rows; Regular only for the
 * expanded DR/legal links.
 */
export const NavPanelContent = ({
  onNavigate,
}: {
  onNavigate?: () => void;
}) => {
  const { t } = useTranslation();
  const { navigationItems, showFriendsWithBenefitsAttentionBubble } =
    usePortalShellConfig();
  const logout = useLogout();
  const legalLinks = getLegalLinksForSidebar(t);

  return (
    <nav
      className="flex flex-col gap-1"
      aria-label={t("layout.menu.sidebar.ariaLabel")}
    >
      <ul className="flex flex-col gap-1">
        {navigationItems.map((item) => (
          <NavItem
            key={item.target}
            item={item}
            onNavigate={onNavigate}
            showFriendsWithBenefitsAttentionBubble={
              showFriendsWithBenefitsAttentionBubble
            }
          />
        ))}
      </ul>

      <hr className="my-2 border-t border-border-tertiary" />

      <ul className="flex flex-col gap-1">
        <NavRow
          href={t("Footer.support.url")}
          onClick={onNavigate}
          icon={<QuestionMark className="size-6" />}
          label={t("Common.support")}
        />
        <Disclosure as="li">
          {({ open }) => (
            <>
              <Disclosure.Button
                className={clsx(
                  // !font-semibold: see NavRow — form.scss button reset
                  "group relative flex w-full items-center gap-2 rounded-md px-2 py-2 text-body-base !font-semibold leading-none text-foreground-primary outline-none",
                  // hover only on devices with a real pointer: on touch,
                  // :hover sticks after a tap, leaving the row permanently
                  // highlighted once the disclosure has been toggled
                  open
                    ? "bg-surface-tertiary"
                    : "[@media(hover:hover)]:hover:bg-surface-tertiary",
                  "active:bg-surface-secondary",
                )}
                tabIndex={KEYBOARD_TAB_INDEX}
              >
                <FocusRing />
                <LogoMonogram className="size-6 text-foreground-primary" />
                {t("Common.logoName")}
                <ChevronDown
                  className={clsx(
                    "ml-auto size-4 text-foreground-primary transition-transform",
                    open && "rotate-180",
                  )}
                />
              </Disclosure.Button>
              <Transition
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Disclosure.Panel as="ul" className="flex flex-col gap-1 pl-8">
                  {legalLinks.map((link) => (
                    <NavRow
                      key={link.label}
                      href={isLegalLinkWithAction(link) ? undefined : link.url}
                      onClick={
                        isLegalLinkWithAction(link)
                          ? () => {
                              link.onClick();
                              onNavigate?.();
                            }
                          : onNavigate
                      }
                      icon={null}
                      label={link.label}
                      regular
                      compact
                    />
                  ))}
                </Disclosure.Panel>
              </Transition>
            </>
          )}
        </Disclosure>
      </ul>

      <hr className="my-2 border-t border-border-tertiary" />

      <ul>
        <NavRow
          onClick={() => {
            logout();
            onNavigate?.();
          }}
          icon={<Exit className="size-6" />}
          label={t("layout.header.menu.logout")}
        />
      </ul>
    </nav>
  );
};
